/**
 * The day's sun for a place, from @archisimple/solar (Sprint 103.0, ADR-0120 Rule 2).
 *
 * Everything astronomical comes from the platform's one sun (ADR-0091): this
 * file only chooses which questions to ask it. No second solar formula exists
 * in this app, so a sunrise here and a sunrise in the editor never disagree.
 */

import {
  dayTimeline,
  isKnownZone,
  localToInstant,
  resolveTimeZone,
  seasonDates,
  seasonOf,
  solarDay,
  sunPosition,
  type DayTimeline,
  type LocalDate,
  type LocalTime,
  type Season,
  type SolarDay,
  type SolarZone
} from '@archisimple/solar';
import type { SkyDirection } from './projection.ts';

export interface Place {
  readonly latitude: number;
  readonly longitude: number;
  /** Metres, when the position came from the device; absent when typed. */
  readonly accuracyMetres?: number;
  readonly source: 'device' | 'manual';
  /** The address or name it was found by, when it came from the search (Sprint 103.1). */
  readonly label?: string;
  /** Its IANA time zone, when the search knows it (a French address is in Paris's). */
  readonly zone?: string;
}

export interface PlaceZone {
  readonly zone: SolarZone;
  /** True when no zone is known and the clock is read from the longitude. */
  readonly approximate: boolean;
}

/**
 * The clock a place's times are read in. Where the device stands, its own zone
 * is the place's; a typed place far away has no zone this app can know without
 * a lookup unless the search gave one, so the time is set from its longitude, as the editor does for a
 * country with no zone (`resolveTimeZone`).
 */
export function zoneOf(place: Place, deviceZone: string | undefined): PlaceZone {
  if (place.zone !== undefined && isKnownZone(place.zone)) {
    return { zone: { kind: 'iana', name: place.zone }, approximate: false };
  }
  if (place.source === 'device' && deviceZone !== undefined && isKnownZone(deviceZone)) {
    return { zone: { kind: 'iana', name: deviceZone }, approximate: false };
  }
  return resolveTimeZone(undefined, place.longitude);
}

export interface Day {
  readonly solar: SolarDay;
  readonly timeline: DayTimeline;
}

export function dayAt(localDate: LocalDate, zone: SolarZone, place: Place): Day {
  const solar = solarDay(localDate, zone, place.latitude, place.longitude);
  return { solar, timeline: dayTimeline(solar) };
}

export function sunAt(
  localDate: LocalDate,
  localTime: LocalTime,
  zone: SolarZone,
  place: Place
): SkyDirection & { readonly aboveHorizon: boolean } {
  const instant = localToInstant(localDate, localTime, zone);
  return sunPosition(instant, place.latitude, place.longitude);
}

export interface HourMark extends SkyDirection {
  readonly hour: number;
}

/** The sun at each whole hour it is up: the dots and labels along the arc. */
export function hourMarks(localDate: LocalDate, zone: SolarZone, place: Place): HourMark[] {
  const marks: HourMark[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const time = `${String(hour).padStart(2, '0')}:00`;
    const sun = sunAt(localDate, time, zone, place);
    if (sun.aboveHorizon) {
      marks.push({
        hour,
        azimuthDegrees: sun.azimuthDegrees,
        altitudeDegrees: sun.altitudeDegrees
      });
    }
  }
  return marks;
}

export type ReferenceKey = 'june-solstice' | 'march-equinox' | 'december-solstice';

export interface ReferencePath {
  readonly key: ReferenceKey;
  /** The season this day is for the place: June is winter in Sydney. */
  readonly season: Season;
  readonly localDate: LocalDate;
  readonly path: readonly SkyDirection[];
}

/**
 * The year's extreme paths and the equinox between them. One equinox stands for
 * both: their paths coincide to well under a degree.
 */
export function referencePaths(year: number, zone: SolarZone, place: Place): ReferencePath[] {
  const wanted: readonly ReferenceKey[] = ['june-solstice', 'march-equinox', 'december-solstice'];
  return seasonDates(year, zone)
    .filter((date): date is typeof date & { season: ReferenceKey } =>
      (wanted as readonly string[]).includes(date.season)
    )
    .map((date) => ({
      key: date.season,
      season: seasonOf(date.season, place.latitude),
      localDate: date.localDate,
      path: solarDay(date.localDate, zone, place.latitude, place.longitude).path
    }));
}

/** The calendar date before `localDate`. */
export function previousDate(localDate: LocalDate): LocalDate {
  const [year, month, day] = localDate.split('-').map(Number);
  const before = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, (day ?? 1) - 1));
  return before.toISOString().slice(0, 10);
}

/** Today's sunrise-to-sunset change, in minutes, against the day before. */
export function dayLengthChange(localDate: LocalDate, zone: SolarZone, place: Place): number {
  const today = solarDay(localDate, zone, place.latitude, place.longitude, { path: false });
  const yesterday = solarDay(previousDate(localDate), zone, place.latitude, place.longitude, {
    path: false
  });
  return today.dayLengthMinutes - yesterday.dayLengthMinutes;
}
